import * as XLSX from 'xlsx';

// Human-readable labels for backend role strings used in export columns.
// Keep in sync with ROLE_DISPLAY_LABELS in StaffAnalytics.tsx.
const ROLE_DISPLAY_LABELS: Record<string, string> = {
  Physician: 'Physician',
  Nurse: 'Nurse',
  Technician: 'Technician',
  Anesthesiologist: 'Anesthetic',
  AdvancedPracticeProvider: 'Advanced Practice Provider',
};

const formatRoleForExport = (role: string): string => {
  if (!role) return '';
  if (ROLE_DISPLAY_LABELS[role]) return ROLE_DISPLAY_LABELS[role];
  const spaced = role.replace(/([a-z])([A-Z])/g, '$1 $2');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};

// Types
export interface LocationHistoryEntry {
  date: string;
  staffId: number;
  staffName: string;
  role: string;
  roomId: number | null;
  roomName: string | null;
  roomType: string | null;
  enteredAt: string;
  exitedAt: string | null;
  durationMinutes: number | null;
  hourlyRate: number | null;
  cost: number | null;
}

export interface LocationHistoryExportData {
  hospitalName: string;
  entries: LocationHistoryEntry[];
  summary: {
    totalEntries: number;
    totalCost: number;
    totalHours: number;
  };
}

export interface LocationHistoryExportResponse {
  staffLocationHistoryExport: LocationHistoryExportData;
}

// Helper functions
export const formatTimestampForExport = (isoString: string | null): string => {
  if (!isoString) return '';
  const date = new Date(isoString);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');
  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
};

export const formatDurationForExport = (minutes: number | null): string => {
  if (minutes === null) return '';
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  if (hours === 0) return `${remainingMinutes}m`;
  return `${hours}h ${remainingMinutes}m`;
};

const EXPORT_HEADERS = [
  'Hospital Name',
  'Date',
  'Staff Name',
  'Role',
  'Room Name',
  'Room Type',
  'In Time',
  'Out Time',
  'Duration',
  'Duration (min)',
  'Hourly Rate ($)',
  'Cost ($)',
];

const OFF_SITE_LABEL = 'Off-site';

const normalizeRoomFields = (
  entry: Pick<LocationHistoryEntry, 'roomId' | 'roomName' | 'roomType'>
): { roomName: string; roomType: string } => {
  // If any room metadata is missing, treat it as off-site.
  const isOffSite = entry.roomId === null || entry.roomName === null || entry.roomType === null;
  if (isOffSite) {
    return { roomName: OFF_SITE_LABEL, roomType: OFF_SITE_LABEL };
  }
  return { roomName: entry.roomName || '', roomType: entry.roomType || '' };
};

const mapEntryToRow = (hospitalName: string, entry: LocationHistoryEntry) => [
  hospitalName,
  entry.date,
  entry.staffName,
  formatRoleForExport(entry.role),
  normalizeRoomFields(entry).roomName,
  normalizeRoomFields(entry).roomType,
  formatTimestampForExport(entry.enteredAt),
  formatTimestampForExport(entry.exitedAt),
  formatDurationForExport(entry.durationMinutes),
  entry.durationMinutes ?? '',
  entry.hourlyRate?.toFixed(2) ?? '',
  entry.cost !== null ? Math.round(entry.cost) : '',
];

// Export functions
export const exportLocationHistoryToCSV = (data: LocationHistoryExportData): void => {
  const rows = data.entries.map(entry => mapEntryToRow(data.hospitalName, entry));

  const worksheet = XLSX.utils.aoa_to_sheet([EXPORT_HEADERS, ...rows]);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Staff Location History');

  const timestamp = Math.floor(Date.now() / 1000);
  XLSX.writeFile(workbook, `staff-location-history-${timestamp}.csv`, { bookType: 'csv' });
};

export const exportLocationHistoryToXLSX = (data: LocationHistoryExportData): void => {
  const rows = data.entries.map(entry => [
    data.hospitalName,
    entry.date,
    entry.staffName,
    formatRoleForExport(entry.role),
    normalizeRoomFields(entry).roomName,
    normalizeRoomFields(entry).roomType,
    formatTimestampForExport(entry.enteredAt),
    formatTimestampForExport(entry.exitedAt),
    formatDurationForExport(entry.durationMinutes),
    entry.durationMinutes ?? '',
    entry.hourlyRate ?? '',
    entry.cost !== null ? Math.round(entry.cost) : '',
  ]);

  const worksheet = XLSX.utils.aoa_to_sheet([EXPORT_HEADERS, ...rows]);

  // Set column widths
  worksheet['!cols'] = [
    { wch: 20 }, // Hospital Name
    { wch: 12 }, // Date
    { wch: 20 }, // Staff Name
    { wch: 15 }, // Role
    { wch: 18 }, // Room Name
    { wch: 15 }, // Room Type
    { wch: 20 }, // In Time
    { wch: 20 }, // Out Time
    { wch: 10 }, // Duration
    { wch: 14 }, // Duration (min)
    { wch: 14 }, // Hourly Rate
    { wch: 10 }, // Cost
  ];

  // Add summary sheet
  const summaryHeaders = ['Metric', 'Value'];
  const summaryRows = [
    ['Total Entries', data.summary.totalEntries],
    ['Total Hours', data.summary.totalHours.toFixed(2)],
    ['Total Cost ($)', Math.round(data.summary.totalCost)],
  ];
  const summarySheet = XLSX.utils.aoa_to_sheet([summaryHeaders, ...summaryRows]);
  summarySheet['!cols'] = [{ wch: 15 }, { wch: 15 }];

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Staff Location History');
  XLSX.utils.book_append_sheet(workbook, summarySheet, 'Summary');

  const timestamp = Math.floor(Date.now() / 1000);
  XLSX.writeFile(workbook, `staff-location-history-${timestamp}.xlsx`);
};
